import { AppStore } from '..';
import errorReporter from '../../utils/errorReporter';

type ProtectedAction =
  | 'checkout'
  | 'verify_email'
  | 'signup'
  | 'login'
  | 'set_password'
  | 'password_reset';

const RECAPTCHA_DISABLED_TOKEN = 'RECAPTCHA_OFF';
const RECAPTCHA_API_UNAVAILABLE_TOKEN = 'RECAPTCHA_API_UNAVAILABLE';

// if window.grecaptcha isn't ready by now, assume it is not available
const RECAPTCHA_READY_TIMEOUT_MS = 3000;
const CHECK_INTERVAL_MS = 200;

export const initGlobalRecaptcha = (store: AppStore) =>
  new Promise<boolean>((resolve) => {
    // determine recaptcha API available once for all recaptcha-dependent UI
    let globalRecaptchaAPIReadyCheck: ReturnType<typeof setInterval> | undefined = undefined;
    let globalRecaptchaAPITimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    // check that this code is running in a browser
    if (typeof window === 'undefined') {
      store.dispatch({
        type: 'SET_RECAPTCHA_API_AVAILABILITY',
        availability: false,
      });
      resolve(false);
      return;
    }

    globalRecaptchaAPITimeout = setTimeout(() => {
      clearInterval(globalRecaptchaAPIReadyCheck);
      clearTimeout(globalRecaptchaAPITimeout);

      store.dispatch({
        type: 'SET_RECAPTCHA_API_AVAILABILITY',
        availability: false,
      });

      resolve(false);
    }, RECAPTCHA_READY_TIMEOUT_MS);

    globalRecaptchaAPIReadyCheck = setInterval(() => {
      if (window.grecaptcha?.enterprise != null) {
        clearInterval(globalRecaptchaAPIReadyCheck);
        clearTimeout(globalRecaptchaAPITimeout);

        store.dispatch({
          type: 'SET_RECAPTCHA_API_AVAILABILITY',
          availability: true,
        });

        resolve(typeof window.grecaptcha.enterprise.ready === 'function');
      }
    }, CHECK_INTERVAL_MS);
  });

class RecaptchaAPIUnavailableError extends Error {
  constructor(args: { action: ProtectedAction }) {
    super(`Recaptcha-protected action '${args.action}' attempted while Recaptcha API not loaded`);
    this.name = 'RecaptchaAPIUnavailableError';
  }
}

export default async function GetRecaptchaVerificationToken(
  store: AppStore,
  args: {
    action: ProtectedAction;
  },
) {
  const apiIsAvailable = await initGlobalRecaptcha(store);

  return new Promise((resolve) => {
    const enableReCAPTCHARemoteData = store.getState().recaptcha.enabled;
    const enableReCAPTCHA = enableReCAPTCHARemoteData.value;

    // try again soon, we're waiting on the backend for "enabled" setting or site key
    if (enableReCAPTCHA == null) {
      setTimeout(() => {
        resolve(GetRecaptchaVerificationToken(store, args));
      }, CHECK_INTERVAL_MS);
    }

    // skip reCAPTCHA verification, reCAPTCHA is disabled for the site
    if (!enableReCAPTCHA) {
      resolve(RECAPTCHA_DISABLED_TOKEN);
      return;
    }

    // api is not available
    if (!apiIsAvailable) {
      errorReporter.notify(new RecaptchaAPIUnavailableError(args));
      resolve(RECAPTCHA_API_UNAVAILABLE_TOKEN);
      return;
    }

    window.grecaptcha?.enterprise.ready(async () => {
      const { action } = args;
      const token = await window.grecaptcha.enterprise.execute(
        process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '',
        {
          action,
        },
      );
      resolve(token);
    });
  });
}
